import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { Col, Row, Space, Tag, Typography } from 'antd';
import dayjs from 'dayjs';

import AccountPlanContext from '@totem/components/accountPlan/AccountPlanContext';
import PlanCard from '@totem/components/accountPlan/PlanCard';
import { WorklistItem } from '@totem/components/accountPlan/types';
import {
  PriorityBadge,
  StatBox,
} from '@totem/components/accountPlan/utilities';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import colors from '@totem/styles/colors';

const OperationalCard = () => {
  const { data } = useContext(AccountPlanContext);
  if (!data) {
    return null;
  }
  const { tickets, vulnerabilities } = data.summary;
  const since = dayjs(tickets.trailingSince).format('MMM D');

  const columns = [
    {
      title: 'Ticket',
      dataIndex: 'number',
      width: 110,
      render: (_: string, item: WorklistItem) => (
        <Link to={`/dashboard/ticket/${item.id}`}>{item.number} ↗</Link>
      ),
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      width: 80,
      render: (priority: string) => <PriorityBadge priority={priority} />,
    },
    {
      title: 'Building',
      dataIndex: 'buildingName',
      width: 150,
      render: (name: string) =>
        name || (
          <Typography.Text type="secondary">Account-level</Typography.Text>
        ),
    },
    {
      title: 'Issue',
      dataIndex: 'summary',
      render: (summary: string, item: WorklistItem) => (
        <Space size={4} wrap>
          {item.kind === 'advisory' && (
            <Tag
              color={colors.branding.accent}
              style={{ color: '#ffffff', fontWeight: 600 }}
            >
              {item.category === 'Optimize' ? 'Optimize finding' : 'Advisory'}
            </Tag>
          )}
          {summary}
        </Space>
      ),
    },
  ];

  const recurrence = tickets.recurrence
    .slice(0, 3)
    .map(
      (group) =>
        `${group.count} ${group.category.toLowerCase()} at ${group.buildingName || 'account-level'}`,
    )
    .join('; ');

  return (
    <PlanCard
      variant="live"
      title="Operational Health & Worklist"
      liveTip="Open tickets and CVEs from the IB Portal. Optimize / advisory projects are the service delivering value, not outages; Operational health is scored on the trailing quarter's incidents."
      portalLink="/dashboard/ticketing"
      extra={`${tickets.open} open tickets · ${tickets.openIncidents} incidents`}
    >
      <Row gutter={10} style={{ marginBottom: 12 }}>
        <Col span={6}>
          <StatBox label="Open tickets" value={tickets.open} />
        </Col>
        <Col span={6}>
          <StatBox
            label="Open incidents"
            value={tickets.openIncidents}
            alert={tickets.openP1Incidents > 0}
          />
        </Col>
        <Col span={6}>
          <StatBox
            label={`P1 incidents since ${since}`}
            value={tickets.trailingP1Incidents}
            alert={tickets.trailingP1Incidents >= 10}
          />
        </Col>
        <Col span={6}>
          <StatBox
            label="Confirmed CVEs"
            value={
              vulnerabilities.scanning
                ? vulnerabilities.confirmedOpen.toLocaleString()
                : '—'
            }
            alert={vulnerabilities.confirmedOpen >= 100}
          />
        </Col>
      </Row>
      <IBTable
        size="small"
        rowKey="id"
        columns={columns}
        dataSource={tickets.worklist}
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: [10, 25, 50],
          hideOnSinglePage: true,
        }}
        locale={{ emptyText: 'No open tickets' }}
      />
      <Typography.Paragraph
        type="secondary"
        style={{ marginTop: 10, marginBottom: 0, fontSize: 12 }}
      >
        {tickets.openAdvisory > 0 &&
          `${tickets.openAdvisory} open Optimize / advisory findings are the value story — close them out with documented savings. `}
        {recurrence ? (
          <>
            <Typography.Text strong style={{ fontSize: 12 }}>
              Recurring since {since}:
            </Typography.Text>{' '}
            {recurrence} ({tickets.trailingIncidents} incidents this quarter).
          </>
        ) : (
          `${tickets.trailingIncidents} incidents since ${since}, no recurring pattern.`
        )}
      </Typography.Paragraph>
    </PlanCard>
  );
};

export default OperationalCard;
